import * as React from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/u;

export const TAG_COLOR_PRESETS: string[] = [
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#eab308",
  "#84cc16",
  "#22c55e",
  "#10b981",
  "#14b8a6",
  "#06b6d4",
  "#0ea5e9",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#a855f7",
  "#d946ef",
  "#ec4899",
  "#f43f5e",
  "#64748b",
  "#374151",
  "#111827",
];

function normalize(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return HEX_PATTERN.test(trimmed) ? trimmed.toLowerCase() : null;
}

export interface ColorPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

export function ColorPicker({
  value,
  onChange,
  className,
  disabled,
  ariaLabel = "Pick color",
}: ColorPickerProps) {
  const normalized = normalize(value);
  const [open, setOpen] = React.useState(false);
  const [hexDraft, setHexDraft] = React.useState(normalized ?? "");

  React.useEffect(() => {
    if (open) setHexDraft(normalized ?? "");
  }, [open, normalized]);

  const commitHex = () => {
    const trimmed = hexDraft.trim();
    if (!trimmed) {
      onChange(null);
      return;
    }
    const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    if (HEX_PATTERN.test(withHash)) {
      onChange(withHash.toLowerCase());
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "flex h-8 items-center gap-2 rounded-md border border-input bg-background px-2 text-sm transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span
            className="inline-block h-4 w-4 shrink-0 rounded-full border"
            style={{
              backgroundColor: normalized ?? "transparent",
              backgroundImage: normalized
                ? undefined
                : "linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%)",
              backgroundSize: "8px 8px",
              backgroundPosition: "0 0, 4px 4px",
            }}
            aria-hidden
          />
          <span className="font-mono text-xs text-muted-foreground">
            {normalized ?? "No color"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">Preset</span>
          {normalized ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <X className="mr-1 h-3 w-3" />
              Clear
            </Button>
          ) : null}
        </div>
        <div className="mb-3 grid grid-cols-10 gap-1.5">
          {TAG_COLOR_PRESETS.map((preset) => {
            const selected = normalized === preset;
            return (
              <button
                key={preset}
                type="button"
                aria-label={preset}
                onClick={() => {
                  onChange(preset);
                  setOpen(false);
                }}
                className="relative flex h-5 w-5 items-center justify-center rounded-full border border-black/10 outline-none ring-offset-background transition hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                style={{ backgroundColor: preset }}
              >
                {selected ? (
                  <Check
                    className="h-3 w-3"
                    style={{ color: "#fff", filter: "drop-shadow(0 0 1px rgba(0,0,0,0.6))" }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Custom</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={normalized ?? "#3b82f6"}
              onChange={(e) => onChange(e.target.value.toLowerCase())}
              className="h-8 w-10 cursor-pointer rounded border border-input bg-background p-0.5"
              aria-label="Custom color"
            />
            <Input
              value={hexDraft}
              onChange={(e) => setHexDraft(e.target.value)}
              onBlur={commitHex}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitHex();
                }
              }}
              placeholder="#aabbcc"
              maxLength={7}
              className="h-8 font-mono text-xs"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
